// API Services for Multi-Franchise Management
// Company, Franchise, and Customer Branch CRUD operations

import { farmbookAxios } from '../../../components/config/App/App';
import type {
  ICompany,
  IFranchise, 
  ICustomerBranch,
  ICompanyFormValues,
  IFranchiseFormValues,
  ICustomerBranchFormValues,
  ICompanyResponse,
  IFranchiseResponse,
  ICustomerBranchResponse,
  IEnhancedUser,
  IAuthResponse
} from '../../../compiler/multiFranchise.interface';

const AUTH_BASE = '/procurement/auth';

// ============================================================================
// COMPANY MANAGEMENT APIs
// ============================================================================

export const APIGetCompanies = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  company_type?: 'franchise' | 'milkmann';
  is_active?: boolean;
}): Promise<ICompanyResponse> => {
  const response = await farmbookAxios.get('/api/companies', { params });
  return response.data;
};

export const APIGetCompanyById = async (uuid: string): Promise<ICompany> => {
  const response = await farmbookAxios.get(`/api/companies/${uuid}`);
  return response.data;
};

export const APICreateCompany = async (data: ICompanyFormValues): Promise<ICompany> => {
  const response = await farmbookAxios.post('/api/companies', data);
  return response.data;
};

export const APIUpdateCompany = async (uuid: string, data: Partial<ICompanyFormValues>): Promise<ICompany> => {
  const response = await farmbookAxios.put(`/api/companies/${uuid}`, data);
  return response.data;
};

export const APIDeleteCompany = async (uuid: string): Promise<void> => {
  await farmbookAxios.delete(`/api/companies/${uuid}`);
};

// ============================================================================
// FRANCHISE MANAGEMENT APIs (Updated for Backend API)
// ============================================================================

export const APIGetFranchises = async (params?: {
  page?: number;
  limit?: number;
  company_uuid?: string;
  search?: string;
  is_active?: boolean;
}): Promise<IFranchiseResponse> => {
  const response = await farmbookAxios.get('/procurement/franchises', { params });
  return response.data;
};

export const APIGetFranchiseById = async (uuid: string): Promise<IFranchise> => {
  const response = await farmbookAxios.get(`/procurement/franchises/${uuid}`);
  return response.data;
};

export const APICreateFranchise = async (data: IFranchiseFormValues): Promise<IFranchise> => {
  const response = await farmbookAxios.post('/procurement/franchises', data);
  return response.data;
};

export const APIUpdateFranchise = async (uuid: string, data: IFranchiseFormValues): Promise<IFranchise> => {
  const response = await farmbookAxios.put(`/procurement/franchises/${uuid}`, data);
  return response.data;
};

export const APIDeleteFranchise = async (uuid: string): Promise<void> => {
  await farmbookAxios.delete(`/procurement/franchises/${uuid}`);
};

// ============================================================================
// CUSTOMER BRANCH MANAGEMENT APIs (Enhanced with franchise support)
// ============================================================================

export const APIGetCustomerBranches = async (params?: any): Promise<ICustomerBranchResponse> => {
  const response = await farmbookAxios.get('/api/branches', { params });
  return response.data;
};

export const APIGetCustomerBranchById = async (uuid: string): Promise<ICustomerBranch> => {
  const response = await farmbookAxios.get(`/api/branches/${uuid}`);
  return response.data;
};

export const APICreateCustomerBranch = async (data: ICustomerBranchFormValues): Promise<ICustomerBranch> => {
  const response = await farmbookAxios.post('/api/branches', data);
  return response.data;
};

export const APIUpdateCustomerBranch = async (uuid: string, data: Partial<ICustomerBranchFormValues>): Promise<ICustomerBranch> => {
  const response = await farmbookAxios.put(`/api/branches/${uuid}`, data);
  return response.data;
};

export const APIDeleteCustomerBranch = async (uuid: string): Promise<void> => {
  await farmbookAxios.delete(`/api/branches/${uuid}`);
};

// Get branches by franchise (for franchise owners)
export const APIGetBranchesByFranchise = async (franchiseUuid: string, params?: {
  page?: number;
  limit?: number;
  search?: string;
  branch_type?: 'normal' | 'hub' | 'warehouse';
  is_active?: boolean;
}): Promise<ICustomerBranchResponse> => {
  const response = await farmbookAxios.get(`/api/franchise/${franchiseUuid}/branches`, { params });
  return response.data;
};

// Bulk operations for branches
export const APIBulkUpdateBranches = async (data: {
  branch_uuids: string[];
  updates: Partial<ICustomerBranchFormValues>;
}): Promise<ICustomerBranch[]> => {
  const response = await farmbookAxios.patch('/api/branches/bulk-update', data);
  return response.data;
};

export const APIBulkDeleteBranches = async (branchUuids: string[]): Promise<void> => {
  await farmbookAxios.post('/api/branches/bulk-delete', { branch_uuids: branchUuids });
};

// ============================================================================
// AUTHENTICATION & USER MANAGEMENT APIs (Enhanced)
// ============================================================================

export const APIGetUserProfile = async (): Promise<IEnhancedUser> => {
  const response = await farmbookAxios.get(`${AUTH_BASE}/user-info`);
  return response.data;
};

export const APIGetUserPermissions = async (): Promise<string[]> => {
  const response = await farmbookAxios.get(`${AUTH_BASE}/user-info`);
  return response?.data?.permissionData || [];
};

export const APILogin = async (credentials: {
  email: string;
  password: string;
}): Promise<IAuthResponse> => {
  const response = await farmbookAxios.post(`${AUTH_BASE}/login`, credentials);
  return response.data;
};

// ============================================================================
// UTILITY APIs
// ============================================================================

// Get franchises available for current user
export const APIGetAvailableFranchises = async (): Promise<IFranchise[]> => {
  try {
    const response = await farmbookAxios.get(`${AUTH_BASE}/available-franchises`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    // Backward compatibility: older API may not expose /auth/available-franchises yet.
    if (error?.response?.status === 404) {
      const fallback = await farmbookAxios.get('/procurement/franchises', {
        params: { page: 1, limit: 300, is_active: true },
      });
      return fallback?.data?.data || [];
    }
    throw error;
  }
};

// Validate franchise access for current user
export const APIValidateFranchiseAccess = async (franchiseUuid: string): Promise<boolean> => {
  try {
    const response = await farmbookAxios.get(`${AUTH_BASE}/validate-franchise-access/${franchiseUuid}`);
    return response.data.has_access;
  } catch (error) {
    return false;
  }
};

// Get provinces for branch location dropdown
export const APIGetProvinces = async (): Promise<{ code: string; name: string }[]> => {
  const response = await farmbookAxios.get('/api/utils/provinces');
  return response.data;
};

// Get districts by province
export const APIGetDistricts = async (provinceCode: string): Promise<{ code: string; name: string }[]> => {
  const response = await farmbookAxios.get(`/api/utils/districts/${provinceCode}`);
  return response.data;
};

// Geocoding service for branch coordinates
export const APIGeocodeAddress = async (address: string): Promise<{
  latitude: number;
  longitude: number;
}> => {
  const response = await farmbookAxios.post('/api/utils/geocode', { address });
  return response.data;
};

// ============================================================================
// EXPORT HELPER FUNCTIONS
// ============================================================================

// Create API client with franchise context
export const createFranchiseAwareAPIClient = (franchiseUuid?: string) => {
  const headers: Record<string, string> = {};
  
  if (franchiseUuid) {
    headers['X-Franchise-Context'] = franchiseUuid;
  }
  
  // Return axios instance with custom headers for this request
  return {
    get: (url: string, config?: any) => farmbookAxios.get(url, { ...config, headers: { ...config?.headers, ...headers } }),
    post: (url: string, data?: any, config?: any) => farmbookAxios.post(url, data, { ...config, headers: { ...config?.headers, ...headers } }),
    put: (url: string, data?: any, config?: any) => farmbookAxios.put(url, data, { ...config, headers: { ...config?.headers, ...headers } }),
    delete: (url: string, config?: any) => farmbookAxios.delete(url, { ...config, headers: { ...config?.headers, ...headers } }),
    patch: (url: string, data?: any, config?: any) => farmbookAxios.patch(url, data, { ...config, headers: { ...config?.headers, ...headers } })
  };
};

// Helper function to handle API errors
export const handleAPIError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};
